/* eslint-disable unicorn/consistent-destructuring */
import angular from 'angular';
import _ from 'lodash-es';
import {
	Keymaps,
	LanguageExtensions,
	Plugins,
	ThemeExtensions,
} from '@knitiv/code-editor';
import kformUtils from '../../utils/kform-utils';
import template from './select.html';

const selectConfig = /* @ngInject */ (/** @type {any} */ KformComponentsProvider) => {
	KformComponentsProvider.register('select', {
		title: 'Select',
		template(/** @type {any} */ $scope) {
			return $scope.component.validate?.card?.isMultiple()
				? template.replace('<ui-select', '<ui-select multiple')
				: template;
		},
		tableView(/** @type {any} */ data, /** @type {any} */ options) {
			const getItem = (/** @type {any} */ itemData) => {
				switch (options.component.dataSrc) {
				case 'nodes': {
					for (const item of options.component.data.values) {
						if (item.kid === itemData) {
							// eslint-disable-next-line no-param-reassign
							itemData = item;
						}
					}
					return itemData;
				}
				case 'values': {
					for (const item of options.component.data.values) {
						if (item.value === itemData) {
							// eslint-disable-next-line no-param-reassign
							itemData = item;
						}
					}
					return itemData;
				}
				case 'json': {
					if (options.component.valueProperty) {
						let selectItems;
						try {
							selectItems = angular.fromJson(options.component.data.json);
						}
						catch {
							selectItems = [];
						}
						for (const item of selectItems) {
							if (item[options.component.valueProperty] === itemData) {
								// eslint-disable-next-line no-param-reassign
								itemData = item;
							}
						}
					}
					return itemData;
				}
				default: {
					return itemData;
				}
				}
			};
			if (options.component.validate?.card?.isMultiple() && Array.isArray(data)) {
				// eslint-disable-next-line unicorn/no-array-reduce,unicorn/no-array-callback-reference
				return data.map(getItem).reduce((previous, item) => {
					const value = typeof item === 'object' ? options.$interpolate(options.component.template)({ item }) : item;
					return (previous === '' ? '' : ', ') + value;
				}, '');
			}

			const item = getItem(data);
			return typeof item === 'object' ? options.$interpolate(options.component.template)({ item }) : item;
		},
		/**
		 * @param {import('angular').IRootScopeService} $rootScope
		 * @param {any} $scope
		 * @param {import('angular').IHttpService} $http
		 * @param {import('angular').IInterpolateService}$interpolate
		 * @param {import('angular').IQService} $q
		 * @param {import('angular').ITimeoutService} $timeout
		 */
		controller: /* @ngInject */ (
			$rootScope,
			$scope,
			$http,
			$interpolate,
			$q,
			$timeout,
		) => {
			// FOR-71 - Skip functionality in the options.building view.
			if ($scope.options && $scope.options.building) {
				return;
			}
			const settings = $scope.component;
			const options = {};
			$scope.nowrap = true;
			$scope.hasNextPage = false;
			$scope.selectItems = [];

			if (settings.autofocus) {
				$timeout(() => {
					const inputs = angular.element(`#form-group-${ settings.key}`).find('input');
					inputs[settings.validate?.card?.isMultiple() ? 0 : 1].focus();
				});
			}

			const initialized = $q.defer();
			initialized.promise
				.then(() => $scope.$emit('selectLoaded', $scope.component))
				.catch(error => console.error(error));

			const { selectValues } = $scope.component;
			const { valueProperty } = $scope.component;
			$scope.getSelectItem = (/** @type {any} */ item) => {
				if (!item) {
					return '';
				}
				if (settings.dataSrc === 'nodes') {
					return item.kid;
				}
				if (settings.dataSrc === 'values') {
					return item.value;
				}

				// Get the item value.
				const itemValue = valueProperty ? _.get(item, valueProperty) : item;
				if (itemValue === undefined) {
					/* eslint-disable no-console */
					console.warn(`Cannot find value property within select: ${ valueProperty}`);
					/* eslint-enable no-console */
				}
				return itemValue;
			};

			$scope.refreshItems = () => $q.resolve([]);
			$scope.$on('refreshList', (
				/** @type {Event} */ event,
				/** @type {string} */ url,
				/** @type {string} */ input,
			) => {
				$scope.refreshItems(input, url);
			});

			let refreshing = false;
			const refreshValue = () => {
				if (refreshing) {
					return;
				}
				refreshing = true;
				const temporaryData = $scope.data[settings.key];
				$scope.data[settings.key] = settings.validate?.card?.isMultiple() ? [] : '';
				if (settings.clearOnRefresh) {
					refreshing = false;
					$scope.$emit('selectLoaded', $scope.component);
					const index = $scope.selectItems.indexOf(temporaryData);
					if (index !== -1) {
						$scope.selectItems.splice(index, 1);
					}
				}
				else {
					$timeout(() => {
						$scope.data[settings.key] = temporaryData;
						refreshing = false;
						$scope.$emit('selectLoaded', $scope.component);
					});
				}
			};

			// Ensures that the value is within the select items.
			const ensureValue = (/** @type {any} */ value) => {
				// eslint-disable-next-line no-param-reassign
				value = _.isNil(value) ? $scope.data[settings.key] : value;
				if (_.isNil(value) || (Array.isArray(value) && value.length === 0)) {
					return;
				}
				if (Array.isArray(value)) {
					// eslint-disable-next-line unicorn/no-array-for-each,unicorn/no-array-callback-reference
					value.forEach(ensureValue);
					return;
				}
				// Iterate through the list of items and see if our value exists...
				let found = false;
				for (let index = 0; index < $scope.selectItems.length; index++) {
					const item = $scope.selectItems[index];
					const selectItem = $scope.getSelectItem(item);
					if (_.isEqual(selectItem, value)) {
						found = true;
						break;
					}
				}

				// If the item is not found in the select items array, then add it manually.
				if (!found) {
					let itemValue = value;
					if (valueProperty) {
						itemValue = {};
						_.set(itemValue, valueProperty, value);
					}
					$scope.selectItems.push(itemValue);
				}
			};

			// Refresh the items when ready.
			const refreshItemsWhenReady = () => {
				initialized.promise
					.then(() => {
						const refreshPromise = $scope.refreshItems(true);
						if (refreshPromise) {
							refreshPromise
								// eslint-disable-next-line promise/no-nesting
								.then(refreshValue)
								// eslint-disable-next-line promise/no-nesting
								.catch((/** @type {any} */ error) => console.error(error));
						}
						else {
							refreshValue();
						}
						return true;
					})
					.catch(error => console.error(error));
			};

			// Add a watch if they wish to refresh on selection of another field.
			if (settings.refreshOn) {
				if (settings.refreshOn === 'data') {
					$scope.$watch('data', refreshItemsWhenReady, true);
					return;
				}

				$scope.$watch(`data.${ settings.refreshOn}`, refreshItemsWhenReady);
				$scope.$watch(`submission.data.${ settings.refreshOn}`, refreshItemsWhenReady);
			}
			else {
				// Watch for the data to be set, and ensure the value is set properly.
				const dataWatch = $scope.$watch(`data.${ settings.key}`, (/** @type {any} */ value) => {
					if (value) {
						initialized.promise
							.then(() => {
								dataWatch();
								return ensureValue();
							})
							.catch(error => console.error(error));
					}
				});
			}

			/** @type {any} */
			let lastInput;
			switch (settings.dataSrc) {
			case 'nodes':
			case 'values': {
				$scope.selectItems = settings.data.values;
				initialized.resolve();
				break;
			}
			case 'json': {
				/** @type {any} */
				let items;

				// Set the new result.
				const setResult = (/** @type {any} */ data, /** @type {boolean} */ append) => {
					// coerce the data into an array.
					if (!Array.isArray(data)) {
						// eslint-disable-next-line no-param-reassign
						data = [data];
					}

					if (data.length < options.params.limit) {
						$scope.hasNextPage = false;
					}
					$scope.selectItems = append ? [...$scope.selectItems, ...data] : data;
				};

				try {
					if (typeof $scope.component.data.json === 'string') {
						items = angular.fromJson($scope.component.data.json);
					}
					else if (typeof $scope.component.data.json === 'object') {
						items = $scope.component.data.json;
					}
					else {
						items = [];
					}

					if (selectValues) {
						// Allow dot notation in the selectValue property.
						if (selectValues.includes('.')) {
							const parts = selectValues.split('.');
							let select = items;
							// eslint-disable-next-line guard-for-in
							for (const index in parts) {
								select = select[parts[index]];
							}
							items = select;
						}
						else {
							items = items[selectValues];
						}
					}
				}
				catch (error) {
					/* eslint-disable no-console */
					console.warn(`Error parsing JSON in ${ $scope.component.key}`, error);
					/* eslint-enable no-console */
					items = [];
				}
				options.params = {
					limit: $scope.component.limit || 20,
					skip: 0,
				};

				$scope.refreshItems = (
					/** @type {string} */ input,
					/** @type {string} */ url,
					/** @type {boolean} */ append,
				) => {
					// If they typed in a search, reset skip.
					if (lastInput !== input) {
						lastInput = input;
						options.params.skip = 0;
					}
					let selectItems = items;
					if (input) {
						selectItems = selectItems.filter((/** @type {any} */ item) => {
							// Get the visible string from the interpolated item.
							const value = $interpolate($scope.component.template)({ item }).replaceAll(/<[\n.]*?>/gm, '');
							switch ($scope.component.filter) {
							case 'startsWith': {
								return value.toLowerCase().lastIndexOf(input.toLowerCase(), 0) === 0;
							}
							case 'contains': {
								return value.toLowerCase().includes(input.toLowerCase());
							}
							default: {
								return value.toLowerCase().lastIndexOf(input.toLowerCase(), 0) === 0;
							}
							}
						});
					}
					// @ts-ignore
					options.params.skip = Number.parseInt(options.params.skip, 10);
					options.params.limit = Number.parseInt(options.params.limit, 10);
					selectItems = selectItems.slice(options.params.skip, options.params.skip + options.params.limit);
					setResult(selectItems, append);
					return initialized.resolve($scope.selectItems);
				};
				$scope.refreshItems();
				break;
			}
			case 'custom': {
				$scope.refreshItems = () => {
					try {
						/* eslint-disable no-unused-vars */
						const data = _.cloneDeep($scope.submission.data);
						const row = _.cloneDeep($scope.data);
						/* eslint-enable no-unused-vars */
						// eslint-disable-next-line no-eval
						$scope.selectItems = eval(`(function(data, row) {
	var values = [];
	${ settings.data.custom.toString() };
	return values;
						})(data, row)`);
					}
					catch {
						$scope.selectItems = [];
					}
					return initialized.resolve($scope.selectItems);
				};
				$scope.refreshItems();
				break;
			}
			default: {
				$scope.selectItems = [];
				initialized.resolve($scope.selectItems);
			}
			}
		},
		selectable: true,
		settings: {
			autofocus: false,
			input: true,
			tableView: true,
			label: 'Select',
			key: 'select',
			placeholder: '',
			data: {
				values: [],
				json: '',
				custom: '',
			},
			dataSrc: 'values',
			valueProperty: '',
			defaultValue: '',
			refreshOn: '',
			filter: '',
			template: '<span>{{ item.label }}</span>',
			protected: false,
			persistent: true,
			hidden: false,
			clearOnHide: true,
			options: {
				collapsed:   null,
				collapsible: null,
			},
			validate: {
				card: '0-1',
			},
			event:        {
				onSelect: null,
			},
			kdata: {
				submit: true,
			},
		},
		icon:          'fa fa-th-list',
		views:         [
			{
				name:     'Display',
				template: 'kform/components/select/display.html',
			},
			{
				name:     'Data',
				template: 'kform/components/select/data.html',
			},
			{
				name:     'Validation',
				template: 'kform/components/select/validate.html',
			},
			{
				name:     'API',
				template: 'kform/components/common/api.html',
			},
			{
				name:     'Layout',
				template: 'kform/components/common/layout.html',
			},
			{
				name:     'Conditional',
				template: 'kform/components/common/conditional.html',
			},
		],
		onEdit: /* @ngInject */ (/** @type {any} */ $scope) => {
			$scope.dataSources = {
				nodes:    'Nodes',
				values:   'Values',
				json:     'Raw JSON',
				custom:   'Custom',
			};
			$scope.resources = [];
			$scope.resourceFields = [];

			$scope.onEditorSetup = () => [
				ThemeExtensions.oneLight,
				LanguageExtensions.json(),

				Plugins.FOLD_GUTTERS(),
				Plugins.MATCH_BRACKETS(),
				Plugins.AUTOCOMPLETION(),
				Plugins.CLOSE_BRACKETS(),
				Plugins.DRAW_SELECTION(),
				Plugins.DROP_CURSOR(),
				Plugins.HIGHLIGHT_ACTIVE_LINE_GUTTER(),
				Plugins.HIGHLIGHT_SELECTION_MATCHES(),
				Plugins.HIGHLIGHT_SPECIAL_CHARS(),
				Plugins.HISTORY(),
				Plugins.INDENT_ON_INPUT(),
				Plugins.MULTIPLE_CURSORS(),
				Plugins.RECTANGULAR_SELECTION(),
				Plugins.LINE_NUMBERS(),
				Plugins.KEYMAP.of(Keymaps.KNITIV_KEYMAP),
				// onChange,
			];

			// Returns only input fields we are interested in.
			const getInputFields = (/** @type {any} */ components) => {
				/** @type {any[]} */
				const fields = [];
				kformUtils.eachComponent(components, (/** @type {any} */ component) => {
					if (
						component.key
						&& component.input
						&& component.type !== 'button'
						&& component.key !== $scope.component.key
					) {
						const comp = _.clone(component);
						if (!comp.label) {
							comp.label = comp.key;
						}
						fields.push(comp);
					}
				});
				return fields;
			};

			$scope.formFields = [{ label: 'Any Change', key: 'data' }, ...getInputFields($scope.form.components)];

			$scope.$watch('component.dataSrc', (/** @type {any} */ source, /** @type {any} */ previousSource) => {
				if (source !== previousSource) {
					$scope.component.template = '<span>{{ item.label }}</span>';
				}
			});

			// Update other parameters when the value property changes.
			$scope.currentValueProperty = $scope.component.valueProperty;
		},
	});
};
export default angular.module('app').config(selectConfig);
